import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { isNullish } from 'remeda';
import {
  getLifecycleErrorMessage,
  WORKFLOW_VERSION_QUERIES,
} from 'src/graphql/entities/workflow/hooks/use-workflow-version-mutations';
import { CREATE_WORKFLOW_WITH_FIRST_VERSION_MUTATION } from 'src/graphql/entities/workflow/workflow.mutations';
import type {
  CreatedWorkflowWithFirstVersion,
  CreateWorkflowWithFirstVersionInput,
  CreateWorkflowWithFirstVersionMutation,
} from 'src/graphql/entities/workflow/workflow.types';
import { useNotifications } from 'src/utils/hooks';

const CREATE_WORKFLOW_ERROR_MESSAGE = 'Failed to create workflow. Please try again.';

interface UseCreateWorkflowResult {
  /**
   * Resolves with the new workflow and version 1 as inserted. `null` on failure, which has
   * already been shown to the user.
   */
  createWorkflowWithFirstVersion: (
    input: Omit<CreateWorkflowWithFirstVersionInput, 'partnerId'>,
  ) => Promise<CreatedWorkflowWithFirstVersion | null>;
  isCreating: boolean;
}

export function useCreateWorkflow({
  partnerId,
}: {
  partnerId?: number | null;
}): UseCreateWorkflowResult {
  const { showErrorAlert } = useNotifications();

  const [mutate, { loading: isCreating }] =
    useMutation<CreateWorkflowWithFirstVersionMutation>(
      CREATE_WORKFLOW_WITH_FIRST_VERSION_MUTATION,
      {
        // Not awaited: these read the reader replica, which usually lags this write, so
        // waiting on them only delays the dialog. The result carries the new rows instead.
        refetchQueries: WORKFLOW_VERSION_QUERIES,
        onError: (error) => {
          console.error(error);
          showErrorAlert(getLifecycleErrorMessage(error, CREATE_WORKFLOW_ERROR_MESSAGE));
        },
      },
    );

  const createWorkflowWithFirstVersion = useCallback(
    async (input: Omit<CreateWorkflowWithFirstVersionInput, 'partnerId'>) => {
      if (isNullish(partnerId)) {
        showErrorAlert('Partner not selected.');
        return null;
      }

      const { data } = await mutate({ variables: { input: { ...input, partnerId } } });

      return data?.createWorkflowWithFirstVersion ?? null;
    },
    [mutate, partnerId, showErrorAlert],
  );

  return { createWorkflowWithFirstVersion, isCreating };
}
